import { Component, forwardRef, inject, Input, signal, ViewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { IFormInput } from '@mwe/models';
import { FormComponent } from '../../form.component';
import { NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import ShortUniqueId from 'short-unique-id';

@Component({
  selector: 'mwe-checkbox-input',
  templateUrl: './checkbox-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: CheckboxInputComponent,
      multi: true,
    },
  ],
  imports: [FormsModule, ReactiveFormsModule, NgClass, TranslatePipe],
})
export class CheckboxInputComponent implements ControlValueAccessor {
  @Input() parentForm: UntypedFormGroup;
  @Input() inputElem: IFormInput;
  @Input() isLoading = false;

  @ViewChild(FormControlDirective, { static: true })
  formControlDirective: FormControlDirective;
  uuid = signal<string>(new ShortUniqueId({ dictionary: 'alpha' }).rnd(8));

  getUniqueId(i: number): string {
    return `checkbox-option-${this.inputElem.name}-${i}`;
  }

  formComponent = inject(
    forwardRef(() => FormComponent),
    { optional: true },
  );

  get isValid(): any {
    return this.parentForm.controls[this.inputElem.name]?.valid;
  }

  isChecked(value: string): boolean {
    const controlValue = this.parentForm.controls[this.inputElem.name]?.value;
    return Array.isArray(controlValue) && controlValue.includes(value);
  }

  onCheckboxChange(value: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const currentValues = this.parentForm.controls[this.inputElem.name]?.value;
    const arrayValues = Array.isArray(currentValues) ? currentValues : [];

    let newValues: string[];
    if (isChecked) {
      newValues = [...arrayValues, value];
    } else {
      newValues = arrayValues.filter((v: string) => v !== value);
    }

    this.parentForm.controls[this.inputElem.name].setValue(newValues);
    this.parentForm.controls[this.inputElem.name].markAsDirty();
    this.parentForm.controls[this.inputElem.name].updateValueAndValidity();
  }

  registerOnTouched(fn: any): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: any): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(obj: any): void {
    this.formControlDirective?.valueAccessor?.writeValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective?.valueAccessor?.setDisabledState(isDisabled);
  }

  get ariaDescribedBy(): string | undefined {
    if (this.formComponent) {
      // Error State hat Vorrang für Screenreader
      if (this.formComponent.isInputErrorVisible(this.inputElem.componentType)) {
        return `form-input-error--${this.inputElem.name}`;
      } else if (this.formComponent.isInputHelpInfoVisible(this.inputElem)) {
        return `form-input-info--${this.inputElem.name}`;
      }
    }
    return undefined;
  }
}
